import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'
import type { AdminSession } from '@/constants/adminSession'
import type { SellerSession } from '@/constants/sellerSession'
import type {
  MallProductPayload,
  MallProductStatus,
  MallSale,
  ProductStockHistory,
  ServerMallProduct,
  UploadedImage,
} from '@/api/mallProduct'

/** 관리자 API. 로그인은 caion_user 계정으로 하고 쿠키는 판매업체·기관회원과 따로 쓴다. */
const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/admin`

const fetchOptions: RequestInit = {
  credentials: 'include',
}

const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

/* ---------------- 로그인 ---------------- */

export interface AdminLoginPayload {
  userId: string
  password: string
  rememberMe?: boolean
}

export async function loginAdmin(payload: AdminLoginPayload) {
  const response = await apiFetch(`${BASE}/login`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ admin: AdminSession }>(response)
  return unwrap(result, '로그인에 실패했습니다.').admin
}

export async function logoutAdmin() {
  const response = await apiFetch(`${BASE}/logout`, { ...fetchOptions, method: 'POST' })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '로그아웃에 실패했습니다.')
  }
}

export async function fetchAdminSession() {
  const response = await apiFetch(`${BASE}/session`, fetchOptions)
  const result = await parseJson<{ admin: AdminSession }>(response)
  return unwrap(result, '관리자 세션을 확인할 수 없습니다.').admin
}

/* ---------------- 상품 관리 ---------------- */

export type ProductStatusCounts = Partial<Record<MallProductStatus, number>>

export async function fetchAdminProducts(query: { status?: string; keyword?: string } = {}) {
  const params = new URLSearchParams()
  if (query.status) params.set('status', query.status)
  if (query.keyword) params.set('keyword', query.keyword)

  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${BASE}/mall/products${suffix}`, fetchOptions)
  const result = await parseJson<{ products: ServerMallProduct[]; counts: ProductStatusCounts }>(response)
  return unwrap(result, '상품 목록을 불러오지 못했습니다.')
}

export async function fetchAdminProduct(code: string) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}`, fetchOptions)
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '상품을 불러오지 못했습니다.').product
}

export async function saveAdminProduct(
  code: string,
  payload: MallProductPayload & { adminMemo?: string },
) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '상품 저장에 실패했습니다.').product
}

export async function setAdminProductStatus(code: string, status: MallProductStatus, memo = '') {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/status`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ status, memo }),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return { product: unwrap(result, '판매 상태 변경에 실패했습니다.').product, message: result.message ?? '' }
}

export async function setAdminProductStock(code: string, stock: number) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/stock`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ stock }),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '재고 저장에 실패했습니다.').product
}

export async function addAdminProductStock(code: string, add: number) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/stock`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ add }),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '재고 추가에 실패했습니다.').product
}

export async function fetchAdminProductStockHistory(code: string) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/stock-history`, {
    ...fetchOptions,
  })
  const result = await parseJson<ProductStockHistory>(response)
  return unwrap(result, '재고 이력을 불러오지 못했습니다.')
}

export async function saveAdminProductRelated(
  code: string,
  payload: { relatedCodes: string[]; substituteCodes: string[] },
) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/related`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '관련상품 저장에 실패했습니다.').product
}

export async function duplicateAdminProduct(code: string) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/duplicate`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return {
    product: unwrap(result, '상품 복제에 실패했습니다.').product,
    message: result.message || '',
  }
}

export async function approveAdminProduct(code: string, memo = '') {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/approve`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ memo }),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return { product: unwrap(result, '승인에 실패했습니다.').product, message: result.message ?? '' }
}

export async function rejectAdminProduct(code: string, reason: string) {
  const response = await apiFetch(`${BASE}/mall/products/${encodeURIComponent(code)}/reject`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '반려에 실패했습니다.').product
}

/* ---------------- 이달의 특가 ---------------- */

export async function fetchAdminDeals() {
  const response = await apiFetch(`${BASE}/mall/deals`, fetchOptions)
  const result = await parseJson<{ products: ServerMallProduct[] }>(response)
  return unwrap(result, '이달의 특가를 불러오지 못했습니다.').products
}

export async function saveAdminDeals(
  codes: string[],
  period: { startAt?: string | null; endAt?: string | null } = {},
) {
  const response = await apiFetch(`${BASE}/mall/deals`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify({
      codes,
      startAt: period.startAt || null,
      endAt: period.endAt || null,
    }),
  })
  const result = await parseJson<{ products: ServerMallProduct[] }>(response)
  return {
    products: unwrap(result, '이달의 특가 저장에 실패했습니다.').products,
    message: result.message ?? '',
  }
}

export async function fetchAdminSales() {
  const response = await apiFetch(`${BASE}/mall/sales`, fetchOptions)
  const result = await parseJson<{ sales: MallSale[] }>(response)
  return unwrap(result, '공동구매 특별할인을 불러오지 못했습니다.').sales
}

export async function createAdminSale(body: { name: string; start: string; end: string }) {
  const response = await apiFetch(`${BASE}/mall/sales`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(body),
  })
  const result = await parseJson<{ sale: MallSale }>(response)
  return unwrap(result, '특별할인을 등록하지 못했습니다.').sale
}

export async function updateAdminSale(code: string, body: { name: string; start: string; end: string }) {
  const response = await apiFetch(`${BASE}/mall/sales/${encodeURIComponent(code)}`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify(body),
  })
  const result = await parseJson<{ sale: MallSale }>(response)
  return unwrap(result, '특별할인을 저장하지 못했습니다.').sale
}

export async function deleteAdminSale(code: string) {
  const response = await apiFetch(`${BASE}/mall/sales/${encodeURIComponent(code)}`, {
    ...jsonOptions,
    method: 'DELETE',
  })
  const result = await parseJson<Record<string, never>>(response)
  if (!result.success) throw new Error(result.message || '특별할인을 삭제하지 못했습니다.')
}

export async function uploadAdminImages(files: File[]) {
  const body = new FormData()
  files.forEach((file) => body.append('images', file))

  const response = await apiFetch(`${BASE}/mall/products/images`, {
    ...fetchOptions,
    method: 'POST',
    body,
  })
  const result = await parseJson<{ images: UploadedImage[] }>(response)
  return unwrap(result, '이미지 업로드에 실패했습니다.').images
}

/* ---------------- 판매업체 ---------------- */

export interface AdminSellerRow extends SellerSession {
  productCount: number
  lastLoginAt: string | null
}

export async function fetchAdminSellers() {
  const response = await apiFetch(`${BASE}/mall/sellers`, fetchOptions)
  const result = await parseJson<{ sellers: AdminSellerRow[] }>(response)
  return unwrap(result, '판매업체 목록을 불러오지 못했습니다.').sellers
}

export async function impersonateAdminSeller(code: string) {
  const response = await apiFetch(`${BASE}/mall/sellers/${encodeURIComponent(code)}/impersonate`, {
    ...jsonOptions,
    method: 'POST',
  })
  const result = await parseJson<{ seller: SellerSession }>(response)
  return unwrap(result, '판매업체 화면을 열지 못했습니다.').seller
}

export async function changeSellerStatus(code: string, status: string, reason = '') {
  const response = await apiFetch(`${BASE}/mall/sellers/${encodeURIComponent(code)}/status`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '상태를 바꾸지 못했습니다.')
  }
}

export interface AdminCreateSellerPayload {
  sellerId: string
  tempPassword: string
}

export async function createAdminSeller(payload: AdminCreateSellerPayload) {
  const response = await apiFetch(`${BASE}/mall/sellers`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ seller: AdminSellerRow }>(response)
  return unwrap(result, '판매업체 등록에 실패했습니다.').seller
}

export interface SellerLegalAcceptance {
  idx: number
  code: string
  version: string
  title?: string
  body?: string
  kind?: 'TERMS' | 'CONTRACT'
  signedAt: string
  signature: string
  pdfPath?: string
  hasPdf?: boolean
}

export async function fetchSellerAcceptances(code: string) {
  const response = await apiFetch(
    `${BASE}/mall/sellers/${encodeURIComponent(code)}/acceptances`,
    fetchOptions,
  )
  const result = await parseJson<{ accepts: SellerLegalAcceptance[] }>(response)
  return unwrap(result, '서명 내역을 불러오지 못했습니다.').accepts
}

export async function downloadSellerAcceptancePdf(
  sellerCode: string,
  idx: number,
  filename = 'contract.pdf',
) {
  const response = await apiFetch(
    `${BASE}/mall/sellers/${encodeURIComponent(sellerCode)}/acceptances/${encodeURIComponent(String(idx))}/pdf`,
    fetchOptions,
  )
  if (!response.ok) {
    let message = '저장된 계약서 PDF를 내려받지 못했습니다.'
    try {
      const json = (await response.json()) as { message?: string }
      if (json?.message) message = json.message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

/* ---- 멘트 · 약관 CMS ---- */

export interface ContentSnippet {
  key: string
  title: string
  group: string
  format: 'text' | 'html'
  body: string
  updatedAt?: string | null
}

export interface LegalDoc {
  code: string
  version: string
  title: string
  body: string
  audience: string
  /** TERMS=이용약관, CONTRACT=공급·판매 계약서 */
  kind?: 'TERMS' | 'CONTRACT'
  active: boolean
  effective: string
}

export async function fetchAdminSnippets(group = '') {
  const query = group ? `?group=${encodeURIComponent(group)}` : ''
  const response = await apiFetch(`${BASE}/content/snippets${query}`, fetchOptions)
  const result = await parseJson<{ snippets: ContentSnippet[] }>(response)
  return unwrap(result, '멘트 목록을 불러오지 못했습니다.').snippets
}

export async function saveAdminSnippet(payload: ContentSnippet) {
  const response = await apiFetch(
    `${BASE}/content/snippets/${encodeURIComponent(payload.key)}`,
    {
      ...jsonOptions,
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  )
  const result = await parseJson<{ snippet: ContentSnippet }>(response)
  return unwrap(result, '멘트 저장에 실패했습니다.').snippet
}

export async function fetchAdminLegalDocs(audience = '') {
  const query = audience ? `?audience=${encodeURIComponent(audience)}` : ''
  const response = await apiFetch(`${BASE}/legal-docs${query}`, fetchOptions)
  const result = await parseJson<{ docs: LegalDoc[] }>(response)
  return unwrap(result, '약관 목록을 불러오지 못했습니다.').docs
}

export async function createAdminLegalDoc(payload: Omit<LegalDoc, 'active'> & { active?: boolean }) {
  const response = await apiFetch(`${BASE}/legal-docs`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ doc: LegalDoc }>(response)
  return unwrap(result, '약관 등록에 실패했습니다.').doc
}

export async function updateAdminLegalDoc(
  code: string,
  version: string,
  payload: Partial<LegalDoc>,
) {
  const response = await apiFetch(
    `${BASE}/legal-docs/${encodeURIComponent(code)}/${encodeURIComponent(version)}`,
    {
      ...jsonOptions,
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  )
  const result = await parseJson<{ doc: LegalDoc }>(response)
  return unwrap(result, '약관 저장에 실패했습니다.').doc
}

export interface AdminOpsKpis {
  pendingPayments: number
  payNotified: number
  claimPending: number
  unshipped: number
  taxInvoicePending: number
  taxInvoiceQueue: AdminTaxInvoiceQueueItem[]
}

export interface AdminTaxInvoiceQueueItem {
  code: string
  orgName: string
  bizName: string
  taxEmail: string
  totalAmount: number
  orderedAt: string
  taxAt: string | null
}

export async function fetchAdminOpsKpis() {
  const response = await apiFetch(`${BASE}/mall/ops-kpis`, fetchOptions)
  const result = await parseJson<AdminOpsKpis>(response)
  return unwrap(result, '운영 KPI를 불러오지 못했습니다.')
}

/* ---------------- 고객관리 · 할인 ---------------- */

export interface AdminMemberTaxRequest {
  orderCode: string
  orderedAt?: string | null
  totalAmount?: number
  orderStatus?: string
  receiver?: string
  phone?: string
  depositor?: string
  taxInvoice?: boolean
  bizNum?: string
  bizName?: string
  bizCert?: string
  taxEmail?: string
  taxDocUrl?: string
  taxStatus?: string
  taxAt?: string | null
  taxRejectReason?: string
}

export interface AdminMemberRow {
  mCode: string
  userId: string
  orgName: string
  longtermCode?: string
  bizNum?: string
  bizCert?: string
  bizGubun?: string
  bossName: string
  bossPhone: string
  bossEmail: string
  orgPhone?: string
  zipcode?: string
  address?: string
  addressDetail?: string
  refundBank?: string
  refundAccount?: string
  refundHolder?: string
  roleCode?: string
  roleName?: string
  signupSource?: string
  emailVerified?: boolean
  phoneVerified?: boolean
  termsAgreed?: boolean
  marketingAgreed?: boolean
  loginType?: string
  oauthEmail?: string
  discountPct: number
  joinedAt?: string | null
  orderCount?: number
  paidCount?: number
  orderAmount?: number
  lastOrderedAt?: string | null
  taxRequests?: AdminMemberTaxRequest[]
}

export async function fetchAdminMembers(keyword = '') {
  const params = new URLSearchParams()
  if (keyword.trim()) params.set('keyword', keyword.trim())
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${BASE}/mall/members${suffix}`, fetchOptions)
  const result = await parseJson<{ members: AdminMemberRow[] }>(response)
  return unwrap(result, '기관 목록을 불러오지 못했습니다.').members
}

export async function fetchAdminMember(mCode: string) {
  const response = await apiFetch(
    `${BASE}/mall/members/${encodeURIComponent(mCode)}`,
    fetchOptions,
  )
  const result = await parseJson<{ member: AdminMemberRow }>(response)
  return unwrap(result, '회원 정보를 불러오지 못했습니다.').member
}

/* ---------------- 문의 · 후기 중재 ---------------- */

export interface AdminCsQna {
  idx: number
  id: string
  productCode: string
  productName: string
  productImage: string
  author: string
  org: string
  secret: boolean
  question: string
  answer: string
  answeredAt: string
  date: string
  storeName: string
  sellerCode: string
  orderCode?: string
  orderAt?: string
  orderStatus?: string
  orderSummary?: string
}

export interface AdminCsReview {
  idx: number
  id: string
  productCode: string
  productName: string
  productImage: string
  author: string
  org: string
  rating: number
  date: string
  content: string
  image: string
  reply: string
  repliedAt: string
  storeName: string
  sellerCode: string
  /** 작성 시 저장한 관련 주문 (없을 수 있음) */
  orderCode?: string
  orderAt?: string
  orderStatus?: string
  orderSummary?: string
}

export async function fetchAdminCs(query: { tab?: 'qna' | 'review'; unanswered?: boolean; keyword?: string } = {}) {
  const params = new URLSearchParams()
  if (query.tab) params.set('tab', query.tab)
  if (query.unanswered) params.set('unanswered', '1')
  if (query.keyword?.trim()) params.set('keyword', query.keyword.trim())
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${BASE}/mall/cs${suffix}`, fetchOptions)
  const result = await parseJson<{
    qnas: AdminCsQna[]
    reviews: AdminCsReview[]
    unansweredQna: number
    unansweredReview: number
  }>(response)
  return unwrap(result, '문의·후기를 불러오지 못했습니다.')
}

export async function deleteAdminCsItem(kind: 'qna' | 'review', idx: number) {
  const response = await apiFetch(`${BASE}/mall/cs/${kind}/${idx}`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<{ ok: boolean }>(response)
  return unwrap(result, '숨김 처리에 실패했습니다.')
}

export async function answerAdminCsQna(idx: number, answer: string) {
  const response = await apiFetch(`${BASE}/mall/cs/qna/${idx}/answer`, {
    ...fetchOptions,
    method: 'POST',
    body: JSON.stringify({ answer }),
  })
  const result = await parseJson<Record<string, never>>(response)
  return unwrap(result, '문의 답변을 등록하지 못했습니다.')
}

/* ---------------- 1:1 상담(상담 게시판) ---------------- */

export interface AdminConsultListItem {
  id: number
  authorId: string
  title: string
  orgName: string
  orgCode: string
  orderCode?: string
  /** PORTAL: 포털 사업 상담 / MALL: 함우리몰 문의 */
  channel?: 'PORTAL' | 'MALL'
  /** 어떤 상담인지. 예: 일반상담, 급식통상임금설명회신청 */
  kind?: string
  /** 테스트 고객(testemp) 글 — 최고관리자에게만 내려간다 */
  isTest?: boolean
  contactPhone: string
  email: string | null
  clientIp: string | null
  status: 'WAIT' | 'DONE' | string
  sellerReplyAllowed?: boolean
  hasExcel: boolean
  excelName: string | null
  createdAt: string
}

export interface AdminConsultResponse {
  id: number
  content: string
  byName: string
  /** 답변 작성자 역할 (기본은 관리자) */
  role?: 'ADMIN' | 'SELLER' | string
  /** 판매업체 답변인 경우 판매업체 코드 */
  bySellerCode?: string
  hasExcel: boolean
  excelName: string | null
  createdAt: string
}

export interface AdminConsultDetail {
  request: {
    id: number
    accessId: string
    authorId: string
    title: string
    orgName: string
    orgCode: string
    orderCode?: string
    /** PORTAL: 포털 사업 상담 / MALL: 함우리몰 문의 */
    channel?: 'PORTAL' | 'MALL'
    kind?: string
    /** 테스트 고객(testemp) 글 — 최고관리자에게만 내려간다 */
    isTest?: boolean
    contactPhone: string
    email: string | null
    clientIp: string | null
    content: string
    status: 'WAIT' | 'DONE' | string
    /** 관리자가 판매업체에게 답변 위임을 켰는가 */
    sellerReplyAllowed?: boolean
    /** 위임을 마지막으로 켠/끈 관리자 ID */
    delegatedBy?: string
    /** 위임 상태를 마지막으로 바꾼 시각 */
    delegatedAt?: string | null
    hasExcel: boolean
    excelName: string | null
    createdAt: string
  }
  responses: AdminConsultResponse[]
}

export async function fetchAdminConsultList(
  query: { page?: number; limit?: number; keyword?: string; status?: string; channel?: string } = {},
) {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.limit) params.set('limit', String(query.limit))
  if (query.keyword?.trim()) params.set('keyword', query.keyword.trim())
  if (query.status) params.set('status', query.status)
  if (query.channel) params.set('channel', query.channel)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${BASE}/consult${suffix}`, fetchOptions)
  const result = await parseJson<{
    items: AdminConsultListItem[]
    unanswered: number
    unansweredPortal?: number
    unansweredMall?: number
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }>(response)
  return unwrap(result, '상담요청 목록을 불러오지 못했습니다.')
}

export async function fetchAdminConsultDetail(id: number) {
  const response = await apiFetch(`${BASE}/consult/${id}`, fetchOptions)
  const result = await parseJson<AdminConsultDetail>(response)
  return unwrap(result, '상담요청 상세를 불러오지 못했습니다.')
}

export async function createAdminConsultResponse(id: number, content: string, excel?: File | null) {
  const body = new FormData()
  body.append('content', content)
  if (excel) {
    body.append('excel', excel)
    body.append('excelOriginalName', excel.name)
  }
  const response = await apiFetch(`${BASE}/consult/${id}/responses`, {
    ...fetchOptions,
    method: 'POST',
    body,
  })
  const result = await parseJson<{ id: number }>(response)
  return unwrap(result, '답변 등록에 실패했습니다.')
}

async function downloadAdminConsultBlob(url: string, filename: string) {
  const response = await apiFetch(url, fetchOptions)
  if (!response.ok) {
    let message = '파일 다운로드에 실패했습니다.'
    try {
      const json = (await response.json()) as { message?: string }
      if (json?.message) message = json.message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

export async function downloadAdminConsultExcel(id: number, filename: string) {
  return downloadAdminConsultBlob(`${BASE}/consult/${id}/excel`, filename)
}

export async function downloadAdminConsultResponseExcel(
  requestId: number,
  responseId: number,
  filename: string,
) {
  return downloadAdminConsultBlob(
    `${BASE}/consult/${requestId}/responses/${responseId}/excel`,
    filename,
  )
}

export async function fetchAdminConsultAiReplies(id: number) {
  const response = await apiFetch(`${BASE}/consult/${id}/ai-replies`, {
    ...fetchOptions,
    method: 'POST',
  })
  const result = await parseJson<{
    suggestions: string[]
    source: 'ai' | 'fallback'
    message: string | null
  }>(response)
  return unwrap(result, 'AI 답변 추천을 불러오지 못했습니다.')
}

/**
 * 1:1 상담 답변 권한을 판매업체에게 위임하거나 회수한다.
 * 관련 주문이 있는 상담만 위임할 수 있고, 실제 답변은 그 주문의 판매업체만 남길 수 있다.
 */
export async function setAdminConsultSellerReplyPermission(id: number, allowed: boolean) {
  const response = await apiFetch(`${BASE}/consult/${id}/seller-reply-permission`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ allowed }),
  })
  const result = await parseJson<{ sellerReplyAllowed: boolean }>(response)
  return unwrap(result, '위임 상태 변경에 실패했습니다.')
}
